import { Shadows, ThemeOptions } from '@mui/material/styles';

type PaletteForOverrides = {
  primary: { main: string; dark: string; darker: string };
  success: { dark: string; darker?: string };
  action: { disabledBackground: string };
  text: { primary: string; secondary: string };
  white: { main: string };
};

/**
 * Builds MUI component style overrides using the theme palette and shadows.
 * Accepts either the full theme Palette or the options object passed to createTheme,
 * so overrides stay aligned with the theme contract and custom keys (e.g. primary.darker) come from module augmentation.
 */
export const createStyleOverrides = (
  palette: PaletteForOverrides,
  shadows: Shadows
): ThemeOptions['components'] => {
  const primaryDark = palette.primary.dark;
  const primaryDarker = palette.primary.darker;

  return {
    MuiButton: {
      styleOverrides: {
        // WCAG 2.2 AA 1.4.3: outlined primary (e.g. Filters button) text/border must meet 4.5:1
        outlinedPrimary: {
          color: primaryDark,
          borderColor: primaryDark,
          transition:
            'color 1s 0.2s ease, border-color 0.15s ease, background-color 0.15s ease',
          '&:hover': {
            borderColor: primaryDarker,
            backgroundColor: 'transparent',
            color: primaryDarker,
          },
        },
        containedPrimary: {
          backgroundColor: primaryDark,
          color: palette.white.main,
          boxShadow: shadows[2],
          transition:
            'background-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease',
          '&.MuiLoadingButton-loading': {
            color: 'transparent',
          },
          '&.MuiLoadingButton-loading.Mui-disabled': {
            color: 'transparent',
          },
          '&.MuiLoadingButton-loading .MuiLoadingButton-label': {
            opacity: 0,
          },
          '&:hover': {
            backgroundColor: primaryDarker,
            boxShadow: shadows[4],
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: shadows[2],
          },
          '&:focus-visible': {
            outline: `2px solid ${palette.primary.main}`,
            outlineOffset: '1px',
          },
          '&.Mui-disabled': {
            backgroundColor: palette.action.disabledBackground,
            // Ensure disabled button text still meets WCAG contrast requirements on the disabled background
            color: `${palette.text.primary}`,
            transform: 'none',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: `${palette.text.primary}`,
          '&.Mui-focused': {
            color: `${palette.text.primary}`,
          },
          '&.MuiInputLabel-shrink': {
            color: `${palette.text.primary}`,
          },
          // Override when FormControl/TextField uses color="primary" so label meets WCAG contrast
          '&.MuiFormLabel-colorPrimary': {
            color: `${palette.text.primary}`,
            '&.Mui-focused': { color: `${palette.text.primary}` },
            '&.MuiInputLabel-shrink': {
              color: `${palette.text.primary}`,
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: `${palette.text.primary}`,
          '&.Mui-focused': {
            color: `${palette.text.primary}`,
          },
          '&.MuiFormLabel-colorPrimary': {
            color: `${palette.text.primary}`,
            '&.Mui-focused': {
              color: `${palette.text.primary}`,
            },
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: {
          sx: {
            color: 'text.primary',
            '&.Mui-focused': { color: 'text.primary' },
            '&.MuiInputLabel-shrink': { color: 'text.primary' },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          // WCAG 2.2 AA 1.4.3: placeholder meets contrast; text.secondary keeps it distinct from typed input (text.primary)
          '&::placeholder': {
            color: palette.text.secondary,
            opacity: 1,
          },
        },
      },
    },
    // WCAG 2.2 AA 1.4.3: dropdown group headers (e.g. Autocomplete groupBy) must meet 4.5:1
    MuiListSubheader: {
      styleOverrides: {
        root: {
          color: palette.text.primary,
        },
      },
    },
    // WCAG 2.2 AA 1.4.3: general tables filter badge (Filters button in DataTables) must meet 4.5:1
    MuiBadge: {
      styleOverrides: {
        badge: {
          '&.MuiBadge-colorPrimary': {
            backgroundColor: primaryDark,
            color: palette.white.main,
          },
          // Invisible badge (e.g. when count is 0) still in DOM; ensure it passes if measured
          '&.MuiBadge-invisible': {
            backgroundColor: primaryDark,
            color: palette.white.main,
          },
        },
      },
    },
    // WCAG 2.2 AA 1.4.3: default link color must meet 4.5:1 (primary.main fails on white)
    MuiLink: {
      styleOverrides: {
        root: {
          color: primaryDark,
          '&:hover': {
            color: primaryDarker,
          },
        },
      },
    },
  };
};
